// src/services/school.service.ts
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  getDoc,
  deleteField,
  arrayUnion,
} from 'firebase/firestore';
import { db } from './firebase';
import { Teacher, TeacherNotification } from '../types';

export interface SchoolBadge {
  id: string;
  name: string;
  icon: string;
  createdBy: string; // teacher UID
  createdAt: string;
  schoolName: string;
}

export interface SchoolTeacherSummary {
  uid: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  classroomCount: number;
  studentCount: number;
  subscriptionStatus?: string;
  isSchoolAdmin: boolean;
}

export interface SchoolStats {
  teacherCount: number;
  totalClassrooms: number;
  totalStudents: number;
  schoolName: string;
}

export interface ClassroomEngagementData {
  classroomId: string;
  classroomName: string;
  teacherName: string;
  studentCount: number;
  activeStudentsThisWeek: number;
  totalXPThisWeek: number;
  totalXPLastWeek: number;
  totalXPAllTime: number;
  avgXPPerStudent: number;
  atRiskStudents: string[];
  dailyXP: { date: string; xp: number }[];
  engagementScore: number; // 0-100
}

export interface SchoolStyle {
  logoUrl?: string;
  backgroundUrl?: string;
  logoMandatory?: boolean;
  backgroundMandatory?: boolean;
}

export class SchoolService {
  /**
   * Normalize a school name for consistent clustering.
   * Trims, lowercases, removes punctuation, and collapses whitespace.
   * "St. Mary's" and "st marys" and "Saint Mary's" all differ, but at least
   * "  Springfield High  " and "springfield high" will match.
   */
  static normalizeSchoolName(name: string): string {
    return name
      .trim()
      .toLowerCase()
      .replace(/[''`]/g, '') // remove apostrophes
      .replace(/[^a-z0-9\s]/g, ' ') // punctuation → space
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Returns true when `domain` is a school/organization domain rather than a
   * free personal email provider. Used to enhance cluster detection.
   */
  static isSchoolDomain(domain: string): boolean {
    if (!domain) return false;
    const GENERIC_DOMAINS = new Set([
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
      'live.com', 'msn.com', 'me.com', 'aol.com', 'protonmail.com',
      'tutanota.com', 'zoho.com', 'yandex.com', 'mail.com', 'googlemail.com',
      'yahoo.es', 'yahoo.fr', 'yahoo.de', 'yahoo.co.uk',
    ]);
    return !GENERIC_DOMAINS.has(domain.toLowerCase());
  }

  /** Sanitize school name for use as Firestore document ID */
  static sanitizeSchoolId(schoolName: string): string {
    return schoolName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 64) || 'default';
  }

  /** Get all teachers registered under the same school name */
  static async getSchoolTeachers(schoolName: string): Promise<SchoolTeacherSummary[]> {
    try {
      const q = query(
        collection(db, 'teachers'),
        where('schoolName', '==', schoolName)
      );
      const snapshot = await getDocs(q);
      const teachers: SchoolTeacherSummary[] = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data() as Teacher;
        const classroomsQ = query(
          collection(db, 'classrooms'),
          where('teacherId', '==', docSnap.id)
        );
        const classroomsSnap = await getDocs(classroomsQ);
        const studentCount = classroomsSnap.docs.reduce(
          (sum, c) => sum + (c.data().students?.length || 0),
          0
        );

        teachers.push({
          uid: docSnap.id,
          username: data.username,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          role: data.role,
          classroomCount: classroomsSnap.size,
          studentCount,
          subscriptionStatus: data.subscriptionStatus,
          isSchoolAdmin: data.role === 'school_admin',
        });
      }

      return teachers;
    } catch (error) {
      console.error('Error getting school teachers:', error);
      return [];
    }
  }

  /** Aggregate stats for a school */
  static async getSchoolStats(schoolName: string): Promise<SchoolStats> {
    const teachers = await this.getSchoolTeachers(schoolName);
    return {
      teacherCount: teachers.length,
      totalClassrooms: teachers.reduce((s, t) => s + t.classroomCount, 0),
      totalStudents: teachers.reduce((s, t) => s + t.studentCount, 0),
      schoolName,
    };
  }

  /**
   * Returns top students across all classrooms in the school, ranked by total XP.
   * Batches Firestore queries in groups of 10 (Firestore 'in' limit).
   */
  static async getSchoolStudentLeaderboard(
    schoolName: string
  ): Promise<{ name: string; xp: number; classroom: string }[]> {
    try {
      const teacherSnap = await getDocs(
        query(collection(db, 'teachers'), where('schoolName', '==', schoolName))
      );
      const teacherIds = teacherSnap.docs.map((d) => d.id);
      if (teacherIds.length === 0) return [];

      // Batch into chunks of 10 (Firestore 'in' limit)
      const chunks: string[][] = [];
      for (let i = 0; i < teacherIds.length; i += 10) chunks.push(teacherIds.slice(i, i + 10));

      const aggregated: Record<string, { xp: number; classroom: string }> = {};

      for (const chunk of chunks) {
        const classSnap = await getDocs(
          query(collection(db, 'classrooms'), where('teacherId', 'in', chunk))
        );
        for (const classDoc of classSnap.docs) {
          const data = classDoc.data();
          const classroomName: string = data.name ?? 'Unknown';
          const dp: Record<string, Record<string, number>> = data.dailyPoints ?? {};
          const totals: Record<string, number> = {};
          Object.values(dp).forEach((dayPts) => {
            Object.entries(dayPts).forEach(([student, xp]) => {
              totals[student] = (totals[student] ?? 0) + xp;
            });
          });
          Object.entries(totals).forEach(([student, xp]) => {
            if (!aggregated[student] || xp > aggregated[student].xp) {
              aggregated[student] = { xp, classroom: classroomName };
            }
          });
        }
      }

      return Object.entries(aggregated)
        .map(([name, { xp, classroom }]) => ({ name, xp, classroom }))
        .sort((a, b) => b.xp - a.xp);
    } catch (error) {
      console.error('Error getting school student leaderboard:', error);
      return [];
    }
  }

  /**
   * Returns the number of teachers sharing the same school name.
   * Used for cluster detection:
   *  0   = no school set
   *  1–2 = small / no unlock
   *  3–4 = preview unlock available
   *  5+  = school plan trial prompt
   */
  static async getSchoolClusterSize(teacher: Teacher): Promise<number> {
    let nameCount = 0;
    if (teacher.schoolName?.trim()) {
      try {
        const normalized = this.normalizeSchoolName(teacher.schoolName);
        // Query by normalized field first (preferred), fall back to raw schoolName
        const normalizedQ = query(
          collection(db, 'teachers'),
          where('schoolNameNormalized', '==', normalized)
        );
        const normalizedSnap = await getDocs(normalizedQ);
        if (normalizedSnap.size > 0) {
          nameCount = normalizedSnap.size;
        } else {
          // Fallback: raw name match (for teachers registered before normalization was added)
          const rawQ = query(
            collection(db, 'teachers'),
            where('schoolName', '==', teacher.schoolName)
          );
          const rawSnap = await getDocs(rawQ);
          nameCount = rawSnap.size;
        }
      } catch {
        nameCount = 0;
      }
    }

    // If name-based cluster is small and teacher has a school domain, also check by email domain
    if (nameCount < 3 && teacher.emailDomain && this.isSchoolDomain(teacher.emailDomain)) {
      try {
        const domainQ = query(
          collection(db, 'teachers'),
          where('emailDomain', '==', teacher.emailDomain)
        );
        const domainSnap = await getDocs(domainQ);
        return Math.max(nameCount, domainSnap.size);
      } catch {
        // domain query failed (e.g. missing index) — return name count
      }
    }

    return nameCount;
  }

  /** Fetch shared badges for a school */
  static async getSchoolBadges(schoolName: string): Promise<SchoolBadge[]> {
    try {
      const schoolId = this.sanitizeSchoolId(schoolName);
      const badgesRef = collection(db, 'schools', schoolId, 'badges');
      const snapshot = await getDocs(badgesRef);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as SchoolBadge));
    } catch {
      return [];
    }
  }

  /** Create a school-shared badge (School Admin only) */
  static async createSchoolBadge(
    schoolName: string,
    badge: { name: string; icon: string },
    createdBy: string
  ): Promise<SchoolBadge> {
    const schoolId = this.sanitizeSchoolId(schoolName);
    const badgeRef = doc(collection(db, 'schools', schoolId, 'badges'));
    const newBadge: SchoolBadge = {
      id: badgeRef.id,
      name: badge.name,
      icon: badge.icon,
      createdBy,
      createdAt: new Date().toISOString(),
      schoolName,
    };
    await setDoc(badgeRef, newBadge);
    return newBadge;
  }

  /** Delete a school-shared badge */
  static async deleteSchoolBadge(schoolName: string, badgeId: string): Promise<void> {
    const schoolId = this.sanitizeSchoolId(schoolName);
    await deleteDoc(doc(db, 'schools', schoolId, 'badges', badgeId));
  }

  /** Promote a teacher to school_admin role (called by SuperAdmin) */
  static async promoteToSchoolAdmin(
    teacherUid: string,
    schoolInfo?: { schoolName: string; schoolDomain?: string }
  ): Promise<void> {
    await updateDoc(doc(db, 'teachers', teacherUid), {
      role: 'school_admin',
      ...(schoolInfo?.schoolName ? { schoolName: schoolInfo.schoolName } : {}),
      ...(schoolInfo?.schoolDomain ? { schoolDomain: schoolInfo.schoolDomain } : {}),
    });
  }

  /** Demote a school_admin back to teacher (called by SuperAdmin) */
  static async demoteFromSchoolAdmin(teacherUid: string): Promise<void> {
    await updateDoc(doc(db, 'teachers', teacherUid), { role: 'teacher' });
  }

  /** Assign an institutional role (principal, vice_principal, coordinator, inspector, counselor) */
  static async assignInstitutionalRole(
    teacherUid: string,
    role: 'teacher' | 'principal' | 'vice_principal' | 'coordinator' | 'inspector' | 'counselor'
  ): Promise<void> {
    await updateDoc(doc(db, 'teachers', teacherUid), { role });
  }

  /** Verify that a teacher is a school_admin */
  static async isSchoolAdmin(teacherUid: string): Promise<boolean> {
    try {
      const snap = await getDoc(doc(db, 'teachers', teacherUid));
      if (!snap.exists()) return false;
      return snap.data().role === 'school_admin';
    } catch {
      return false;
    }
  }

  /** Assign a list of teachers to a school (bulk). */
  static async bulkUpdateTeacherSchool(teacherUids: string[], schoolName: string): Promise<void> {
    const normalized = SchoolService.normalizeSchoolName(schoolName);
    await Promise.all(
      teacherUids.map((uid) =>
        updateDoc(doc(db, 'teachers', uid), { schoolName, schoolNameNormalized: normalized })
      )
    );
  }

  /** Assign a single teacher to a school (or clear if schoolName is empty). */
  static async updateTeacherSchool(teacherUid: string, schoolName: string): Promise<void> {
    const normalized = schoolName ? SchoolService.normalizeSchoolName(schoolName) : '';
    await updateDoc(doc(db, 'teachers', teacherUid), { schoolName, schoolNameNormalized: normalized });
  }

  /**
   * Soft-unlink a teacher from their school.
   * Clears schoolName, schoolNameNormalized, schoolDomain and resets role to 'teacher'.
   * Does NOT touch classes, students, or subscription status.
   */
  static async removeTeacherFromSchool(teacherUid: string): Promise<void> {
    await updateDoc(doc(db, 'teachers', teacherUid), {
      schoolName: deleteField(),
      schoolNameNormalized: deleteField(),
      schoolDomain: deleteField(),
      role: 'teacher',
    });
  }

  /**
   * Returns per-classroom engagement metrics for all classrooms in a school.
   * Computes XP, active students, at-risk students, and daily XP for the last 14 days.
   */
  static async getClassroomEngagementData(schoolName: string): Promise<ClassroomEngagementData[]> {
    try {
      const teacherSnap = await getDocs(
        query(collection(db, 'teachers'), where('schoolName', '==', schoolName))
      );
      const teacherIds = teacherSnap.docs.map((d) => d.id);
      if (teacherIds.length === 0) return [];

      const teacherNameMap: Record<string, string> = {};
      teacherSnap.docs.forEach((d) => {
        const data = d.data() as Teacher;
        teacherNameMap[d.id] = `${data.firstName || ''} ${data.lastName || data.username || ''}`.trim();
      });

      // Last 14 days (oldest → newest)
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const dateStrings: string[] = [];
      for (let i = 13; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(now.getDate() - i);
        dateStrings.push(d.toISOString().split('T')[0]);
      }

      const result: ClassroomEngagementData[] = [];
      const chunks: string[][] = [];
      for (let i = 0; i < teacherIds.length; i += 10) chunks.push(teacherIds.slice(i, i + 10));

      for (const chunk of chunks) {
        const classSnap = await getDocs(
          query(collection(db, 'classrooms'), where('teacherId', 'in', chunk))
        );
        for (const classDoc of classSnap.docs) {
          const data = classDoc.data();
          const classroomName: string = data.name ?? 'Unnamed';
          const teacherId: string = data.teacherId ?? '';
          const students: string[] = data.students ?? [];
          const studentCount = students.length;
          const dp: Record<string, Record<string, number>> = data.dailyPoints ?? {};

          // Daily XP for last 14 days
          const dailyXP = dateStrings.map((date) => ({
            date,
            xp: dp[date]
              ? Object.values(dp[date]).reduce((a: number, b: number) => a + b, 0)
              : 0,
          }));

          const thisWeekDates = dateStrings.slice(7);  // last 7 days
          const lastWeekDates = dateStrings.slice(0, 7); // 7–14 days ago

          const totalXPThisWeek = dailyXP.slice(7).reduce((s, d) => s + d.xp, 0);
          const totalXPLastWeek = dailyXP.slice(0, 7).reduce((s, d) => s + d.xp, 0);

          let totalXPAllTime = 0;
          Object.values(dp).forEach((dayPts) => {
            Object.values(dayPts).forEach((xp) => { totalXPAllTime += xp as number; });
          });

          const activeThisWeek = new Set<string>();
          thisWeekDates.forEach((date) => {
            const dayPts = dp[date];
            if (dayPts) Object.keys(dayPts).forEach((s) => activeThisWeek.add(s));
          });

          const activePrevWeek = new Set<string>();
          lastWeekDates.forEach((date) => {
            const dayPts = dp[date];
            if (dayPts) Object.keys(dayPts).forEach((s) => activePrevWeek.add(s));
          });

          const atRiskStudents = students.filter(
            (s) => activePrevWeek.has(s) && !activeThisWeek.has(s)
          );

          const avgXPPerStudent = studentCount > 0 ? Math.round(totalXPAllTime / studentCount) : 0;
          const engagementScore =
            studentCount > 0
              ? Math.round((activeThisWeek.size / studentCount) * 100)
              : 0;

          result.push({
            classroomId: classDoc.id,
            classroomName,
            teacherName: teacherNameMap[teacherId] || 'Unknown',
            studentCount,
            activeStudentsThisWeek: activeThisWeek.size,
            totalXPThisWeek,
            totalXPLastWeek,
            totalXPAllTime,
            avgXPPerStudent,
            atRiskStudents,
            dailyXP,
            engagementScore,
          });
        }
      }

      return result.sort((a, b) => b.totalXPThisWeek - a.totalXPThisWeek);
    } catch (error) {
      console.error('Error getting classroom engagement data:', error);
      return [];
    }
  }

  /** Get school branding/style settings */
  static async getSchoolStyle(schoolName: string): Promise<SchoolStyle> {
    try {
      const schoolId = this.sanitizeSchoolId(schoolName);
      const snap = await getDoc(doc(db, 'schools', schoolId));
      if (!snap.exists()) return {};
      const d = snap.data();
      return {
        logoUrl: d.logoUrl,
        backgroundUrl: d.backgroundUrl,
        logoMandatory: d.logoMandatory,
        backgroundMandatory: d.backgroundMandatory,
      };
    } catch { return {}; }
  }

  /** Save school branding/style settings */
  static async setSchoolStyle(schoolName: string, style: SchoolStyle): Promise<void> {
    const schoolId = this.sanitizeSchoolId(schoolName);
    await setDoc(
      doc(db, 'schools', schoolId),
      { ...style, schoolName, updatedAt: new Date().toISOString() },
      { merge: true }
    );
  }

  /**
   * Compare a teacher's classroom metrics against the school average.
   * Returns null if schoolName is missing or fewer than 2 teachers are found
   * (no meaningful comparison available).
   */
  static async getTeacherBenchmark(
    teacher: Teacher,
    teacherClassrooms: any[]
  ): Promise<{
    teacherAvgStudents: number;
    teacherClassCount: number;
    schoolAvgStudents: number;
    schoolAvgClassrooms: number;
    schoolTeacherCount: number;
  } | null> {
    if (!teacher.schoolName) return null;
    try {
      const schoolTeachers = await this.getSchoolTeachers(teacher.schoolName);
      if (schoolTeachers.length < 2) return null;

      const schoolAvgStudents = schoolTeachers.reduce((s, t) => s + t.studentCount, 0) / schoolTeachers.length;
      const schoolAvgClassrooms = schoolTeachers.reduce((s, t) => s + t.classroomCount, 0) / schoolTeachers.length;

      const teacherAvgStudents = teacherClassrooms.length > 0
        ? teacherClassrooms.reduce((s: number, c: any) => s + (c.students?.length ?? 0), 0) / teacherClassrooms.length
        : 0;

      return {
        teacherAvgStudents: Math.round(teacherAvgStudents),
        teacherClassCount: teacherClassrooms.length,
        schoolAvgStudents: Math.round(schoolAvgStudents),
        schoolAvgClassrooms: Math.round(schoolAvgClassrooms * 10) / 10,
        schoolTeacherCount: schoolTeachers.length,
      };
    } catch {
      return null;
    }
  }

  /**
   * Notify site admins and school admins when a new teacher with a school email
   * joins a cluster. Called non-blocking from AuthService.register().
   */
  static async notifyAdminsOfNewClusterMember(newTeacher: Teacher): Promise<void> {
    if (!newTeacher.schoolNameNormalized) return;
    if (!newTeacher.emailDomain || !SchoolService.isSchoolDomain(newTeacher.emailDomain)) return;

    const notification: TeacherNotification = {
      id: `notif-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: 'new_teacher_joined',
      message: `${newTeacher.firstName || newTeacher.username} joined your school (${newTeacher.schoolName})`,
      createdAt: new Date().toISOString(),
      read: false,
    };

    const [schoolAdminsSnap, siteAdminsSnap] = await Promise.all([
      getDocs(query(
        collection(db, 'teachers'),
        where('role', '==', 'school_admin'),
        where('schoolNameNormalized', '==', newTeacher.schoolNameNormalized)
      )),
      getDocs(query(collection(db, 'teachers'), where('isAdmin', '==', true))),
    ]);

    const adminUids = new Set<string>();
    schoolAdminsSnap.docs.forEach((d) => adminUids.add(d.id));
    siteAdminsSnap.docs.forEach((d) => adminUids.add(d.id));
    adminUids.delete(newTeacher.uid);

    await Promise.all(
      [...adminUids].map((uid) =>
        updateDoc(doc(db, 'teachers', uid), { notifications: arrayUnion(notification) }).catch(() => {})
      )
    );
  }
}
